import React, { useState, useEffect, useMemo } from "react";
import {
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  CircularProgress,
  Box,
  Typography,
  Button,
  ButtonGroup,
} from "@mui/material";
import { API_URLS } from "../../config/apiUrls";
import CardNotificacao from "../CardNotificacao";

// Certifique-se de que a interface Notificacao esteja completa e correta
interface Notificacao {
  id: string;
  title: string;
  content: string;
  startDate: string;
  endDate: string;
  maxViews: number;
  imageUrl: string | null;
  isForAllUsers: boolean;
  listCallsCount: number;
  specificCallsCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  targetUsers?: {
    id: string;
    name: string;
    email: string;
  }[];
}

interface ListarNotificacaoProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  refreshTrigger?: number;
}

const ListarNotificacao: React.FC<ListarNotificacaoProps> = ({
  token,
  role,
  refreshTrigger,
}) => {
  const [allNotifications, setAllNotifications] = useState<Notificacao[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URLS.notifications}/admin`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao carregar notificações.");
      }

      const data: Notificacao[] = await response.json();
      setAllNotifications(data);
    } catch (err: any) {
      setError(err.message || "Erro desconhecido ao buscar notificações.");
      console.error("Erro ao buscar notificações:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, [token, refreshTrigger]);

  // Use useMemo para filtrar as notificações apenas quando as dependências mudarem
  const filteredNotifications = useMemo(() => {
    if (filterStatus === "all") {
      return allNotifications;
    } else if (filterStatus === "active") {
      return allNotifications.filter((notif) => notif.isActive);
    } else {
      // filterStatus === "inactive"
      return allNotifications.filter((notif) => !notif.isActive);
    }
  }, [allNotifications, filterStatus]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Carregando notificações...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error" variant="h6">
          Erro: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Botões de filtro */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <ButtonGroup
          variant="contained"
          aria-label="notification filter buttons"
        >
          <Button
            onClick={() => setFilterStatus("all")}
            color={filterStatus === "all" ? "primary" : "secondary"}
          >
            Todas
          </Button>
          <Button
            onClick={() => setFilterStatus("active")}
            color={filterStatus === "active" ? "primary" : "secondary"}
          >
            Ativas
          </Button>
          <Button
            onClick={() => setFilterStatus("inactive")}
            color={filterStatus === "inactive" ? "primary" : "secondary"}
          >
            Inativas
          </Button>
        </ButtonGroup>
      </Box>

      {/* Exibição condicional para quando não há notificações filtradas */}
      {filteredNotifications.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" color="text.secondary">
            Nenhuma notificação{" "}
            {filterStatus === "active"
              ? "ativa"
              : filterStatus === "inactive"
              ? "inativa"
              : ""}{" "}
            encontrada.
          </Typography>
        </Box>
      ) : (
        // SUBSTITUINDO GRID CONTAINER E GRID ITEM POR BOX FLEXBOX
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            margin: "-12px", // Simula spacing={3} (24px, compensado com -12px)
          }}
        >
          {filteredNotifications.map((notification) => (
            // Substituindo <Grid item xs={12} sm={6} md={4}>
            <Box
              key={notification.id}
              sx={{
                padding: "12px", // Simula o spacing={3}
                // Larguras para simular xs={12}, sm={6}, md={4}
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "30%",
                },
              }}
            >
              <CardNotificacao
                notification={notification}
                token={token}
                onNotificationActionSuccess={fetchNotifications}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ListarNotificacao;
